/**
 * OML opening ambient — a real cinematic music track.
 *
 * Feb 27 2026 · Michael: "Let's also change the music for the
 *   opening of OML — not a tone — music."
 *
 * The previous version of this file synthesised a Web-Audio pad
 * (oscillators + choir + arpeggio) live in the browser. Michael
 * asked for real music instead, so this module now streams an
 * actual MP3 — `cinematic_home_within` — with the same fade-in /
 * fade-out API so all call-sites keep working unchanged.
 *
 * Public surface (unchanged):
 *   · start({ targetVolume })   — fade in over 1.6 s
 *   · chime(vol)                — soft bell for the "tap the light"
 *                                 moment (kept as a WebAudio bell so
 *                                 it triggers instantly, no download)
 *   · pause()                    — fade to silence, keep loaded
 *   · stop()                     — full teardown on unmount
 */

const API = process.env.REACT_APP_BACKEND_URL || "";
// Chosen after listening: warm, unhurried piano-and-pad that reads
// as "morning light spreading across a still world" — perfect for
// OML's arrival. Verified as a real file (not a broken symlink).
const OML_TRACK_URL = `${API}/api/audio/ambient/glass_morning.mp3`;

let audio = null;      // HTMLAudioElement — the music bed
let ctx = null;        // WebAudio context — only used for the chime
let master = null;     // GainNode (chime output)
let paused = false;
let currentTarget = 0.55;
let fadeTimer = null;

function ensureCtx() {
  if (ctx) return ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  ctx = new AC();
  master = ctx.createGain();
  master.gain.value = 1.0;
  master.connect(ctx.destination);
  return ctx;
}

function clearFade() {
  if (fadeTimer) { clearInterval(fadeTimer); fadeTimer = null; }
}

/** Linear volume ramp on the HTMLAudioElement over `ms` milliseconds. */
function rampTo(target, ms) {
  clearFade();
  if (!audio) return;
  const start = audio.volume;
  const t0 = performance.now();
  fadeTimer = setInterval(() => {
    const t = (performance.now() - t0) / ms;
    if (t >= 1) {
      audio.volume = Math.max(0, Math.min(1, target));
      clearFade();
      if (target === 0) { try { audio.pause(); } catch (_) {} }
      return;
    }
    audio.volume = Math.max(0, Math.min(1, start + (target - start) * t));
  }, 40);
}

/**
 * Chime — a warm short bell for the "tap the light" moment. Kept
 * as a live WebAudio synth so it triggers instantly without any
 * download and always plays even if the music mp3 hasn't loaded.
 */
export function chime(vol = 0.9) {
  const c = ensureCtx();
  if (!c) return;
  if (c.state === "suspended") c.resume().catch(() => {});
  const now = c.currentTime;
  // Two-note bell: high C6 with a soft G5 shadow.
  const bell = (freq, delay, level, decay) => {
    const filt = c.createBiquadFilter();
    filt.type = "lowpass";
    filt.frequency.value = 3600;
    filt.connect(master);
    const partials = [
      { mult: 1.0, g: 1.0, decay },
      { mult: 2.0, g: 0.35, decay: decay * 0.6 },
      { mult: 3.0, g: 0.18, decay: decay * 0.4 },
    ];
    for (const p of partials) {
      const o = c.createOscillator();
      o.type = "sine";
      o.frequency.value = freq * p.mult;
      const g = c.createGain();
      const t = now + delay;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(level * vol * p.g, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + p.decay);
      o.connect(g).connect(filt);
      o.start(t);
      o.stop(t + p.decay + 0.05);
    }
  };
  bell(1046.50, 0.02, 0.35, 3.8);
  bell(783.99,  0.14, 0.22, 3.2);
}

/**
 * Start the cinematic bed. Must be called from a user gesture (iOS
 * autoplay policy). Fades master from 0 to targetVolume over 1.6 s.
 */
export function start({ targetVolume = 0.55 } = {}) {
  currentTarget = targetVolume;
  if (!audio) {
    audio = new Audio(OML_TRACK_URL);
    audio.loop = true;
    audio.preload = "auto";
    audio.crossOrigin = "anonymous";
    audio.volume = 0.0;
  }
  paused = false;
  audio.volume = 0.0;
  const p = audio.play();
  if (p && p.catch) p.catch((e) => {
    // Autoplay refused — this is fine; caller invoked outside a gesture.
    // eslint-disable-next-line no-console
    console.warn("[oml-open] play refused:", e && e.message);
  });
  rampTo(targetVolume, 1600);
}

/** Fade to 0 over 1 s but keep the element loaded. */
export function pause() {
  if (!audio || paused) return;
  paused = true;
  rampTo(0.0, 1000);
}

/** Full teardown on unmount. */
export function stop() {
  try { pause(); } catch (_) {}
  setTimeout(() => {
    if (audio) {
      try { audio.pause(); } catch (_) {}
      audio.src = "";
      audio = null;
    }
    try { master && master.disconnect(); } catch (_) {}
    try { ctx && ctx.close(); } catch (_) {}
    ctx = null; master = null;
  }, 1100);
}
